import { ReactNode, useCallback, useEffect, useRef, useState } from 'react'

import { ValidationContext } from './context/ValidationContext'
import { TProcess } from './models/IValidationContext'
import { IControllerResult, IValidateResult, ValidationStatus } from './models/IValidationStatus'

interface IControllerProps {
  children: ReactNode
  onValidationStatusChange?: (obj: IControllerResult) => void
  skipUnmountProcess?: boolean // TODO: remove
}

interface IProcess extends IValidateResult {
  process: TProcess
}

export const Controller = ({ children, onValidationStatusChange }: IControllerProps) => {
  const processesRef = useRef<{ [key: string]: IProcess }>({})
  const isMountedRef = useRef<boolean>(false)
  const isUnmountedRef = useRef<boolean>(false)
  const waitMountStatus = useRef<ValidationStatus | undefined>(undefined)
  const statusRef = useRef<ValidationStatus | undefined>(undefined)

  const [processArray, setProcessArray] = useState<IValidateResult[]>([])

  const prepareProcess = useCallback((precesses: { [key: string]: IProcess }) => {
    return Object.values(precesses).map((el) => {
      return {
        field: el.field,
        status: el.status,
        text: el.text,
      }
    })
  }, [])

  const updateProcessArray = useCallback(() => {
    if (isMountedRef.current) {
      const newValue = prepareProcess(processesRef.current)

      setProcessArray((prev) => {
        if (JSON.stringify(prev) !== JSON.stringify(newValue)) {
          return newValue
        }
        return prev
      })
    }
  }, [prepareProcess])

  const callCallback = useCallback(
    (newStatus: ValidationStatus) => {
      if (statusRef.current !== newStatus) {
        statusRef.current = newStatus
        if (isMountedRef.current) {
          if (onValidationStatusChange) {
            onValidationStatusChange({
              status: newStatus,
              processes: processArray,
            })
          }
        } else {
          waitMountStatus.current = newStatus
        }
      }
    },
    [processArray, onValidationStatusChange]
  )

  useEffect(() => {
    const process = isMountedRef.current ? processArray : prepareProcess(processesRef.current)
    const newStatus = process.reduce((prev, current) => {
      if (
        prev === ValidationStatus.Invalid ||
        (current && current.status === ValidationStatus.Invalid)
      ) {
        return ValidationStatus.Invalid
      }
      if (prev === ValidationStatus.WIP || (current && current.status === ValidationStatus.WIP)) {
        return ValidationStatus.WIP
      }
      return ValidationStatus.Valid
    }, ValidationStatus.Valid)

    callCallback(newStatus)
  }, [callCallback, prepareProcess, processArray])

  useEffect(() => {
    return () => {
      isUnmountedRef.current = true
    }
  }, [])

  useEffect(() => {
    if (!isMountedRef.current && waitMountStatus.current) {
      isMountedRef.current = true
      if (onValidationStatusChange) {
        onValidationStatusChange({
          status: waitMountStatus.current,
          processes: processArray,
        })
      }
      waitMountStatus.current = undefined
    }
  }, [processArray, onValidationStatusChange])

  return (
    <ValidationContext.Provider
      value={{
        validationResult: isMountedRef.current ? processArray : [],
        register: () => {
          // Invalid while wait new validation result
          callCallback(ValidationStatus.Invalid)
          const processId = '__validator-' + Math.floor(Math.random() * 10000000)
          let wasUnmounted = false

          const initCurrentProcess = (process: TProcess) => {
            processesRef.current[processId] = {
              field: processId,
              status: ValidationStatus.WIP,
              text: '',
              process,
            }
            updateProcessArray()
          }

          const callProcess = (process: Promise<IValidateResult>) => {
            if (wasUnmounted) {
              return
            }
            const updateCurrentProcess = (data: IValidateResult) => {
              // validator could be removed while validator function was executing
              if (processesRef.current[processId]) {
                processesRef.current[processId].field = data.field
                processesRef.current[processId].status = data.status
                processesRef.current[processId].text = data.text
                updateProcessArray()
              }
            }

            process
              .then((result) => {
                updateCurrentProcess(result)
              })
              .catch((e) => {
                updateCurrentProcess({
                  field: processesRef.current[processId]?.field || '',
                  status: ValidationStatus.Invalid,
                  text: e.message,
                })
              })
          }

          initCurrentProcess(callProcess)
          return {
            process: callProcess,
            unregister: () => {
              delete processesRef.current[processId]
              updateProcessArray()
              wasUnmounted = true
            },
          }
        },
      }}
    >
      {children}
    </ValidationContext.Provider>
  )
}
